/**
 * Task #1328 — ModelPicker: select condiviso per scegliere un modello AI dal
 * catalog dinamico, con sezioni "Consigliati" e "Tutti gli altri" collassabili.
 *
 * Stand-alone (no react-query). Wrappa `useModelCatalog`.
 * Stile: usa `Select` di @easyflow/ui-frontend per non introdurre nuovi paradigmi UI.
 *
 * Uso tipico:
 *   <ModelPicker
 *     capability="chat"
 *     provider="openai"
 *     serviceKey="workflow_ai_agent"
 *     value={model}
 *     onChange={setModel}
 *   />
 */
import * as React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  useModelCatalog,
  type ModelCapability,
  type ModelProviderKey,
  type ModelOption,
} from '../hooks/useModelCatalog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';

export interface ModelPickerProps {
  capability: ModelCapability;
  provider?: ModelProviderKey;
  serviceKey?: string;
  value: string;
  onChange: (modelId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Filtra fuori i deprecati dal listing (default: mostrati con badge). */
  hideDeprecated?: boolean;
  className?: string;
}

function badgeVariantFor(kind: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (kind) {
    case 'recommended': return 'default';
    case 'deprecated':  return 'destructive';
    case 'preview':     return 'secondary';
    default:            return 'outline';
  }
}

interface ItemRowProps { opt: ModelOption }
function ItemRow({ opt }: ItemRowProps): JSX.Element {
  const visibleBadges = opt.badges.slice(0, 3);
  return (
    <div className="flex w-full items-center justify-between gap-2">
      <span className="truncate">{opt.label}</span>
      {visibleBadges.length > 0 && (
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {visibleBadges.map((b) => (
            <Badge
              key={`${b.kind}-${b.label}`}
              variant={badgeVariantFor(b.kind)}
              className="text-[10px] leading-none px-1.5 py-0.5"
              title={b.tooltip ?? b.label}
            >
              {b.label}
            </Badge>
          ))}
        </span>
      )}
    </div>
  );
}

export function ModelPicker(props: ModelPickerProps): JSX.Element {
  const {
    capability, provider, serviceKey, value, onChange,
    placeholder = 'Seleziona modello…', disabled, hideDeprecated = true, className,
  } = props;

  const { data, isLoading, isError } = useModelCatalog({ capability, provider, serviceKey });

  const allOptions: ModelOption[] = data?.options ?? [];
  // Nasconde i deprecati di default, ma mantiene SEMPRE visibile il modello
  // attualmente selezionato (anche se deprecato) col proprio badge, così la
  // scelta corrente non sparisce mai dal picker.
  const filtered = hideDeprecated
    ? allOptions.filter((o) => !o.deprecated || o.value === value)
    : allOptions;
  const recommended = filtered.filter((o) => o.recommended);
  const others      = filtered.filter((o) => !o.recommended);

  const placeholderText = isLoading
    ? 'Caricamento modelli…'
    : isError
      ? 'Errore catalog (riprova)'
      : placeholder;

  // Sezioni collassabili: "Consigliati" sempre aperta, "Tutti gli altri"
  // chiusa di default per ridurre rumore visivo quando ci sono molti modelli.
  const [showRecommended, setShowRecommended] = React.useState<boolean>(true);
  const [showOthers, setShowOthers] = React.useState<boolean>(recommended.length === 0);

  // Warning console quando il catalogo filtrato è vuoto (smoke criteria #1328)
  React.useEffect(() => {
    if (!isLoading && !isError && data && filtered.length === 0) {
      // eslint-disable-next-line no-console -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
      console.warn('[ModelPicker] catalog vuoto per', { capability, provider, serviceKey });
    }
  }, [isLoading, isError, data, filtered.length, capability, provider, serviceKey]);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled || isLoading}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholderText} />
      </SelectTrigger>
      <SelectContent>
        {recommended.length > 0 && (
          <SelectGroup>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setShowRecommended((v) => !v); }}
              className="flex w-full items-center gap-1 px-2 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              {showRecommended ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              <SelectLabel className="px-0 py-0">Consigliati ({recommended.length})</SelectLabel>
            </button>
            {showRecommended && recommended.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <ItemRow opt={opt} />
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {others.length > 0 && (
          <SelectGroup>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setShowOthers((v) => !v); }}
              className="flex w-full items-center gap-1 px-2 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              {showOthers ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              <SelectLabel className="px-0 py-0">
                {recommended.length > 0 ? 'Tutti gli altri' : 'Modelli'} ({others.length})
              </SelectLabel>
            </button>
            {showOthers && others.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <ItemRow opt={opt} />
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="px-3 py-2 text-sm text-muted-foreground">
            Nessun modello disponibile
          </div>
        )}
      </SelectContent>
    </Select>
  );
}

export default ModelPicker;
